import { useState } from 'react';

const ContactUs = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    smsConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Enter a valid email address';
    }
    // Phone number is optional per 10DLC compliance — but validate format if provided
    if (formData.phoneNumber.trim() && !/^\+?[\d\s\-()]{7,15}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Enter a valid phone number';
    }
    // SMS consent is only required if a phone number is provided
    if (formData.phoneNumber.trim() && !formData.smsConsent) {
      newErrors.smsConsent = 'You must agree to the SMS terms to continue';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitted(true);
  };

  return (
    <div>
      {/* Navigation */}
      <nav id="navbar">
        <div className="nav-container">
          <a href="/" className="logo">
            <div className="logo-icon">
              <i className="fas fa-rocket"></i>
            </div>
            <span className="logo-text">Verca</span>
          </a>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="/" className="nav-link">
              <i className="fas fa-home"></i>
              <span>Home</span>
            </a>
            <a href="#features" className="nav-link">
              <i className="fas fa-sparkles"></i>
              <span>Features</span>
            </a>
            <a href="mailto:support@appverca.com" className="nav-link nav-cta">
              <i className="fas fa-envelope"></i>
              <span>Contact</span>
            </a>
          </div>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas fa-${mobileMenuOpen ? 'times' : 'bars'}`}></i>
          </button>
        </div>
      </nav>

      {/* Form Section */}
      <div className="page-body">
        <div className="form-card">
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>Thank you!</h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px' }}>
                We've received your information and will be in touch soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h2 className="form-heading">Contact Us</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? 'input-error' : ''}
                  />
                  {errors.firstName && <span className="error-msg">{errors.firstName}</span>}
                </div>
                <div className="form-group">
                  <label>Last Name <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? 'input-error' : ''}
                  />
                  {errors.lastName && <span className="error-msg">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Email Address <span className="required-star">*</span></label>
                <input
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'input-error' : ''}
                />
                {errors.email && <span className="error-msg">{errors.email}</span>}
              </div>

              {/* Phone number is optional per 10DLC webform compliance */}
              <div className="form-group">
                <label>Phone Number <span className="optional-tag">(optional)</span></label>
                <input
                  type="tel"
                  name="phoneNumber"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className={errors.phoneNumber ? 'input-error' : ''}
                />
                {errors.phoneNumber && <span className="error-msg">{errors.phoneNumber}</span>}
              </div>

              {/* 10DLC Compliant SMS Opt-In Consent — only for text messages */}
                <div className={`consent-box ${errors.smsConsent ? 'consent-error' : ''}`}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="smsConsent"
                      checked={formData.smsConsent}
                      onChange={handleChange}
                    />
                    <span className="consent-text">
                      By checking this box and submitting this form, you consent to receive
                      recurring automated marketing text messages (e.g. promotions, updates,
                      and reminders) from <strong>Verca</strong> at the phone number provided,
                      including messages sent by autodialer. Consent is not a condition of
                      purchase. Message and data rates may apply. Message frequency varies.
                      Carriers are not liable for delayed or undelivered messages. Reply{' '}
                      <strong>HELP</strong> for help. Reply <strong>STOP</strong> to cancel at
                      any time. View our{' '}
                      <a href="terms.html" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="privacy.html" target="_blank" rel="noopener noreferrer">
                        Privacy Policy 
                      </a>.
                      <br /><br />
                      *Your mobile information will not be sold or shared with third parties
                      for promotional or marketing purposes.
                    </span>
                    
                  </label>
                  {errors.smsConsent && <span className="error-msg">{errors.smsConsent}</span>}
                </div>

              <button type="submit" className="submit-btn">Submit</button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-column">
            <div className="footer-logo">
              <div className="logo-icon">
                <i className="fas fa-rocket"></i>
              </div>
              <span className="logo-text">Verca</span>
            </div>
            <p className="footer-tagline">Powering Business and Clients</p>
          </div>
          <div className="footer-column">
            <h4>Legal</h4>
            <a href="privacy.html" className="footer-link">Privacy Policy</a>
            <a href="terms.html" className="footer-link">Terms of Service</a>
          </div>
          <div className="footer-column">
            <h4>Support</h4>
            <a href="mailto:support@appverca.com" className="footer-link">Contact Us</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Verca. All rights reserved.</p>
        </div>
      </footer>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #0F0E17;
          color: #fff;
        }

        /* ── Nav ── */
        nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          padding: 20px 40px;
          background: rgba(15,14,23,0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          z-index: 1000;
        }
        .nav-container {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -1px;
          text-decoration: none;
          color: #fff;
        }
        .logo-icon {
          width: 44px; height: 44px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .logo-icon i { font-size: 22px; color: white; }
        .logo-text { color: #FFFFFF; }

        .nav-links { display: flex; gap: 32px; align-items: center; }
        .nav-link {
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          font-size: 15px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 8px;
        }
        .nav-link i { font-size: 14px; opacity: 0.8; }
        .nav-link:hover { color: #fff; background: rgba(255,255,255,0.05); }
        .nav-cta {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 600;
        }

        .mobile-menu-btn {
          display: none;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          font-size: 20px;
          cursor: pointer;
          padding: 10px 14px;
          border-radius: 10px;
        }

        /* ── Page Body ── */
        .page-body {
          min-height: 100vh;
          background: #0F0E17;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 120px 20px 80px;
        }

        .form-card {
          width: 100%;
          max-width: 520px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 40px 36px;
        }

        .form-heading {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 28px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: rgba(255,255,255,0.8);
          margin-bottom: 6px;
        }
        .required-star {
          color: #FF6B6B;
          font-weight: 700;
        }
        .optional-tag {
          color: rgba(255,255,255,0.35);
          font-weight: 400;
          font-size: 13px;
        }
        .form-group input {
          width: 100%;
          padding: 12px 14px;
          background: rgba(255,255,255,0.05);
          border: 1.5px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #fff;
          font-size: 15px;
          outline: none;
          font-family: inherit;
        }
        .form-group input::placeholder { color: rgba(255,255,255,0.25); }
        .form-group input:focus {
          border-color: #667eea;
          background: rgba(102,126,234,0.06);
        }
        .form-group input.input-error {
          border-color: #FF6B6B;
        }

        .error-msg {
          display: block;
          font-size: 13px;
          color: #FF6B6B;
          margin-top: 4px;
        }

        /* ── Consent ── */
        .consent-box {
          margin-bottom: 28px;
          padding: 16px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
        }
        .consent-box.consent-error {
          border-color: #FF6B6B;
        }
        .checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
        }
        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: #667eea;
          flex-shrink: 0;
          cursor: pointer;
        }
        .consent-text {
          font-size: 13px;
          color: rgba(255,255,255,0.6);
          line-height: 1.6;
        }
        .consent-text strong {
          color: rgba(255,255,255,0.85);
          font-weight: 600;
        }
        .consent-text a {
          color: #667eea;
          text-decoration: underline;
        }

        /* ── Submit ── */
        .submit-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
        }

        /* ── Footer ── */
        footer {
          padding: 80px 40px 40px;
          background: #0F0E17;
          border-top: 1px solid rgba(255,255,255,0.08);
        }
        .footer-content {
          max-width: 1400px;
          margin: 0 auto 48px;
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 48px;
        }
        .footer-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .footer-tagline { color: rgba(255,255,255,0.85); margin-bottom: 24px; }
        .footer-column h4 {
          font-size: 14px;
          margin-bottom: 24px;
          color: rgba(255,255,255,0.7);
          text-transform: uppercase;
          letter-spacing: 1.2px;
          font-weight: 700;
        }
        .footer-link {
          display: block;
          color: rgba(255,255,255,0.65);
          text-decoration: none;
          margin-bottom: 14px;
          font-size: 15px;
        }
        .footer-link:hover { color: #fff; }
        .footer-bottom {
          padding-top: 48px;
          border-top: 1px solid rgba(255,255,255,0.05);
          text-align: center;
          color: rgba(255,255,255,0.65);
          font-size: 14px;
        }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          nav { padding: 16px 24px; }
          .mobile-menu-btn { display: block; }
          .nav-links {
            display: none;
            position: absolute;
            top: 100%; left: 0; right: 0;
            background: rgba(15,14,23,0.98);
            backdrop-filter: blur(20px);
            flex-direction: column;
            padding: 20px;
            gap: 16px;
            border-bottom: 1px solid rgba(255,255,255,0.1);
          }
          .nav-links.mobile-open { display: flex; }
          .nav-link { width: 100%; justify-content: center; }

          .page-body { padding: 120px 16px 60px; }
          .form-card { padding: 32px 24px; }
          .form-row { grid-template-columns: 1fr; }
          .footer-content { grid-template-columns: 1fr; text-align: center; }
        }
      `}</style>

      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
    </div>
  );
};

export default ContactUs;